import React from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Container, Title, Button, Group, Breadcrumbs, Anchor, Loader, Center } from '@mantine/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IconEdit, IconArrowLeft } from '@tabler/icons-react'
import { useAppTranslation } from 'hooks/useTranslation'

import { getCampaignDetail } from 'supabase/campaigns/queries'
import { getCampaignEngagementStats } from 'supabase/campaigns/analytics'
import { CampaignDetailView } from 'components/campaigns/CampaignDetailView'
import { CampaignCreativeAssignment } from 'components/campaigns/CampaignCreativeAssignment'

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useAppTranslation()
  const queryClient = useQueryClient()

  const { data: campaign, isLoading, error } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => getCampaignDetail(id!),
    enabled: !!id,
    // Creative text is edited on another page; the language warning must follow it on return.
    refetchOnMount: 'always'
  })

  const { data: stats } = useQuery({
    queryKey: ['campaign-engagement', id],
    queryFn: () => getCampaignEngagementStats(id!),
    enabled: !!id,
    refetchInterval: 60000 // Auto-refresh every 60 seconds
  })

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    )
  }

  if (error || !campaign) {
    return (
      <Container>
        <Title order={3} c="red">
          {t('campaigns.error_loading') || 'Error loading campaign'}
        </Title>
        <Button mt="md" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/campaigns')}>
          {t('campaigns.back_to_list') || 'Back to Campaigns'}
        </Button>
      </Container>
    )
  }

  const breadcrumbs = [
    { title: t('common.home') || 'Home', href: '/' },
    { title: t('campaigns.title') || 'Campaigns', href: '/campaigns' },
    { title: campaign.name, href: '#' }
  ]

  return (
    <Container size="xl">
      <Breadcrumbs mb="md">
        {breadcrumbs.map((item, index) => (
          index < breadcrumbs.length - 1 ? (
            <Anchor key={index} component={Link} to={item.href}>
              {item.title}
            </Anchor>
          ) : (
            <span key={index} style={{ color: 'var(--mantine-color-dimmed)' }}>
              {item.title}
            </span>
          )
        ))}
      </Breadcrumbs>

      <Group justify="space-between" mb="xl">
        <Title order={2}>{campaign.name}</Title>
        <Group>
          <Button
            variant="light"
            leftSection={<IconEdit size={16} />}
            onClick={() => navigate(`/campaigns/${id}/edit`)}
          >
            {t('common.edit') || 'Edit'}
          </Button>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/campaigns')}
          >
            {t('common.back') || 'Back'}
          </Button>
        </Group>
      </Group>

      <CampaignDetailView campaign={campaign} stats={stats} />

      {/* Creative Assignment Section */}
      <div style={{ marginTop: '2rem' }}>
        <Title order={3} mb="md">{t('campaigns.manage_creatives') || 'Manage Creatives'}</Title>
        <CampaignCreativeAssignment
          campaignId={id!}
          advertiserId={campaign.advertiser_id}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['campaign', id] })}
        />
      </div>
    </Container>
  )
}